import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { trackSegment } from '~/components/SegmentAnalytics';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Divider } from '~/eds/src/components/Divider/Divider';
import { Label } from '~/eds/src/components/Label/Label';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Select } from '~/eds/src/components/Select/Select';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { Filter } from '~/evifields/src/types/Filter';
import {
  ClauseOperatorId,
  ClauseValue,
  TextSearch,
} from '~/evifields/src/types/Value';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { Nullable } from '~/types/Nullable';
import { Option } from '~/types/Option';

import {
  getTextSearchOptionsLookup,
  toTextSearchOption,
  toTextSearchPair,
} from '../../../../evifields/src/utils/textSearch';
import { Field } from '../types';

interface Props {
  field: Field<'clause'>;
  filter: Filter<ClauseValue>;
  onChange: (updatedValues: ClauseValue[]) => void;
}

export const ClauseValues = ({ field, filter, onChange }: Props) => {
  const intl = useIntl();
  const { settings } = field;
  const { clauses } = settings;
  const { id, values, operatorId } = filter;
  const [clauseValue] = values;
  const textSearch = clauseValue?.text_search[0] || null;
  const provisionValue = clauseValue?.provision || null;
  const hasProvision = Boolean(provisionValue);
  const isContainsAny = operatorId === 'contains_any';
  const shouldEnableSearchWithClause = hasProvision && isContainsAny;
  const user = useCurrentUser();

  useEffect(() => {
    if (!isContainsAny) {
      handleCancelClauseSearch();
    }
  }, [isContainsAny]);

  const [draftClauseOperatorId, setDraftClauseOperatorId] =
    useState<Nullable<ClauseOperatorId>>(null);
  const [draftClauseText, setDraftClauseText] =
    useState<Nullable<string>>(null);

  const [
    shouldSearchWithinClause,
    _toggleSearchWithinClause,
    enableSearchWithinClause,
    disableSearchWithinClause,
  ] = useToggle(false);

  useEffect(() => {
    resetDraft(textSearch);
  }, [textSearch]);

  // reset draft state based on textSearch derived from prop.
  const resetDraft = (textSearch: Nullable<TextSearch>) => {
    let updatedDraftClauseOperatorId = null;
    let updatedDraftClauseText = null;
    if (textSearch) {
      updatedDraftClauseOperatorId = toTextSearchOption(textSearch, intl).value;
      updatedDraftClauseText = textSearch.text;
    }

    setDraftClauseOperatorId(updatedDraftClauseOperatorId);
    setDraftClauseText(updatedDraftClauseText);
  };

  const handleClauseProvisionChange = (
    updatedClauseProvision: Nullable<string>,
  ) => {
    if (updatedClauseProvision !== null) {
      const updatedClause = {
        provision: updatedClauseProvision,
        text_search: [],
      };
      onChange([updatedClause]);
    }
  };

  const handleCancelClauseSearch = () => {
    trackSegment('Clause Type - Cancel', {
      user_id: user.id,
      client_id: user.client,
    });
    resetDraft(textSearch);
    disableSearchWithinClause();
  };

  const handleConfirmClauseSearch = () => {
    trackSegment('Clause Type - Confirm', {
      user_id: user.id,
      client_id: user.client,
    });

    let updatedTextSearch: TextSearch[] = [];
    if (draftClauseOperatorId) {
      const [contains, scope] = toTextSearchPair(draftClauseOperatorId, intl);
      updatedTextSearch = [{ contains, scope, text: draftClauseText || '' }];
    }
    const updatedClauseValues: ClauseValue[] = provisionValue
      ? [
          {
            ...clauseValue,
            provision: provisionValue,
            text_search: updatedTextSearch,
          },
        ]
      : [];

    onChange(updatedClauseValues);
    disableSearchWithinClause();
  };

  const options = useMemo(
    () => clauses.map((clause) => ({ value: clause, label: clause })),
    [clauses],
  );

  const clauseContent = shouldSearchWithinClause ? (
    <Layout direction="column" px={px} spacing={2}>
      <Label>{provisionValue}</Label>
      <ClauseOperatorSelect
        value={draftClauseOperatorId}
        onChange={setDraftClauseOperatorId}
      />
      <TextArea
        name="clause-text"
        value={draftClauseText}
        onChange={setDraftClauseText}
      />
    </Layout>
  ) : (
    <Select
      enableOptionIndicator
      enableSearchIcon
      isEmbedded
      isClearable={false}
      isMulti={false}
      name={id}
      options={options}
      placeholder={intl.formatMessage({
        id: 'ClauseValues.searchClausePlaceholder',
        defaultMessage: 'Search for a clause...',
        description: 'Placeholder text for clause search input',
      })}
      value={provisionValue}
      onChange={handleClauseProvisionChange}
    />
  );

  const actions = shouldSearchWithinClause ? (
    <Layout preset="buttons">
      <Button preset="cancel" onClick={handleCancelClauseSearch} />
      <Button
        disabled={!(draftClauseOperatorId && draftClauseText)}
        text={intl.formatMessage({
          id: 'ClauseValues.confirmButtonText',
          defaultMessage: 'Confirm',
          description: 'Button text to confirm clause search',
        })}
        variant="action"
        onClick={handleConfirmClauseSearch}
      />
    </Layout>
  ) : (
    shouldEnableSearchWithClause && (
      <Layout direction="column" spacing={2}>
        <Divider />
        <Box px={px}>
          <Button
            text={intl.formatMessage({
              id: 'ClauseValues.searchWithinClauseText',
              defaultMessage: 'Search within clause text',
              description: 'Button text to enable searching within clause text',
            })}
            variant="action"
            onClick={enableSearchWithinClause}
          />
        </Box>
      </Layout>
    )
  );

  return (
    <Box mx={-px}>
      {clauseContent}
      {actions}
    </Box>
  );
};

interface ClauseOperatorSelectProps {
  value: Nullable<ClauseOperatorId>;
  onChange: (updatedClauseOperatorId: Nullable<ClauseOperatorId>) => void;
}

const ClauseOperatorSelect = ({
  value,
  onChange,
}: ClauseOperatorSelectProps) => {
  const intl = useIntl();

  const searchOptionsLookup: Record<
    string,
    Record<string, Option<ClauseOperatorId>>
  > = {
    contains: getTextSearchOptionsLookup('contains', intl),
    does_not_contain: getTextSearchOptionsLookup('does_not_contain', intl),
  };

  const clauseOptions = Object.values(searchOptionsLookup).reduce(
    (acc, textSearchScopeLookup) => {
      return [...acc, ...Object.values(textSearchScopeLookup)];
    },
    [] as Option<ClauseOperatorId>[],
  );

  return (
    <Select
      enablePortal
      isClearable={false}
      isMulti={false}
      isSearchable={false}
      name="clause-operator"
      options={clauseOptions}
      placeholder={intl.formatMessage({
        id: 'Filters_Deprecated.ClauseValues.chooseOperatorPlaceholder',
        defaultMessage: 'Choose an operator',
        description: 'Placeholder text for clause operator selection',
      })}
      value={value}
      onChange={onChange}
    />
  );
};

const px = 4;
